import {useDeleteWellMutation,} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";

export type UseWellUpdateProps = {}

export type UseWellUpdateResult = {
  deleteWell: (wellId: string, onSuccess: (() => void) | undefined) => void
}

export const useWellUpdate = (props: UseWellUpdateProps = {}): UseWellUpdateResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_deleteWell, {isLoading: isDeleting}] = useDeleteWellMutation()

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useWellUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.well", "Well"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useWellUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useWellUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.well", "Well"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useWellUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.well", "Well") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((wellId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteWell({id: wellId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteWell, handleDeleteResult])

  const deleteWell = useCallback((wellId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.well", "Well"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.well.confirm", "Are you sure you want to delete this well?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(wellId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    deleteWell: deleteWell,
  }
}
